import { Component, AfterContentChecked, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, UrlSegment, Params } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';

import {
    HisUiService, HisAuthService, HisBaseGlobalsService, HisNotificationService, DynamicComponent
} from 'hisng';

import { DynamicFormGroupUtils } from 'app/fs/res/common/service/dynamic-content/dynamic-form-group-utils';
import { DynamicContentUtils } from 'app/fs/res/common/service/dynamic-content/dynamic-content-utils';
import { DocumentHelperService } from 'app/fs/res/common/service/documents/document-helper.service';
import { DynamicContentService } from 'app/fs/res/common/service/dynamic-content/dynamic-content.service';
import { DocumentMetadata } from 'app/fs/res/common/model/documents/documentMetadata';
import { ResTabMenuItem } from 'app/fs/res/common/model/core/tab-menu-item';

import { PatentService } from 'app/fs/res/patent/service/patent/patent.service';
import { Patent } from 'app/fs/res/patent/model/patent/patent';
import { PATEN_REGISTRATIONTYPE_COUNTRY_SPECIFIC } from '../../model/constants';
import { PatentInventor } from '../../model/patent/patent-classes';

@Component( {
    selector: 'res-patent-edit-form',
    templateUrl: './patent-edit-form.component.html',
} )
export class PatentEditFormComponent implements AfterContentChecked, OnInit, OnDestroy {
    public patent: Patent = new Patent();
    public documentsToSave: DocumentMetadata[] = [];
    public tabMenuItems: ResTabMenuItem[];
    public selectedIndex = 0;
    public showBackButton = false;
    public showSearchButton = true;
    public reloadTrigger = true;

    private isEditMode: boolean;
    private priorityPatentId: number;
    private subscription: Subscription = new Subscription();
    private readonly DATA_SERVICE_KEY_PATENT = 'patent';
    private readonly PATEN_REGISTRATIONTYPE_COUNTRY_SPECIFIC = PATEN_REGISTRATIONTYPE_COUNTRY_SPECIFIC;

    constructor( private location: Location,
        private route: ActivatedRoute,
        public hisUiService: HisUiService,
        public auth: HisAuthService,
        public hisBaseGlobalsService: HisBaseGlobalsService,
        private notificationService: HisNotificationService,
        private documentsHelperService: DocumentHelperService,
        private dynamicContentService: DynamicContentService,
        private dynamicContentUtils: DynamicContentUtils,
        private dynamicFormGroupUtils: DynamicFormGroupUtils,
        private patentService: PatentService ) {
    }

    ngAfterContentChecked() {
        // dynamic content is only accessible with module 'HISINONERES'
        if ( !this.hisBaseGlobalsService.isAnyModuleActive( 'HISINONERES' ) ) {
            /* eslint no-underscore-dangle: ["error", { "allow": ["_dynamicComponent"] }] */
            this.patent._dynamicComponent = null;
        }
        this.dynamicContentUtils.setupDynamicTabsHeader(this.patent._dynamicComponent, this.tabMenuItems);
    }

    ngOnInit() {
        this.initializeTabMenuItems();

        this.route.url.subscribe( ( u: UrlSegment[] ) => {
            this.route.queryParams.subscribe( (queryParams: Params) => {
                this.showBackButton = queryParams.showBackButton ? queryParams.showBackButton === 'true' : this.showBackButton;
                this.showSearchButton = queryParams.showSearchButton ? queryParams.showSearchButton === 'true' : this.showSearchButton;
            } );
            if ( u[0].path === 'create' ) {
                this.isEditMode = false;
                this.route.params.subscribe( (params: Params) => {
                    this.subscription = this.patentService.getNewPatent().subscribe( (patent: Patent) => {
                        this.patent = patent;
                        this.priorityPatentId = Number.parseInt( params.priorityPatentId, 10 );
                        // generate dynamic content
                        this.generateDynamicContent();
                        if ( this.priorityPatentId ) {
                            // set priorityPatent values
                            this.setPriorityPatentValues( this.priorityPatentId );
                        }
                        console.log( '/create', this.patent );
                    } );
                } );
            }
            if ( u[0].path === 'edit' ) {
                this.isEditMode = true;
                this.route.params.subscribe( (params: Params) => {
                    console.log( '/edit/:patentId', params.patentId );
                    let patentId = Number.parseInt( params.patentId, 10 );
                    this.patent.id = patentId;
                    this.subscription = this.patentService.getPatentById( patentId ).subscribe( (patent: Patent) => {
                        this.patent = patent;
                        this.formatDateValues( patent );
                        console.log( 'THIS PATENT: ', this.patent );
                    } );
                } );
            }
        } );
    }

    /* save action for patent */
    public save() {
        // set project dynamic component object values
        this.dynamicFormGroupUtils.setDynamicComponentObject( this.patent );
        // eslint-disable-next-line no-underscore-dangle
        console.log( 'patent._dynamicComponent before save =', this.patent._dynamicComponent );

        console.log( 'save() Patent: ', this.patent );
        this.patentService.savePatent( this.patent ).subscribe( (savedPatent: Patent) => {
            this.patent = savedPatent;
            // necessary for p-calendar since it only displays date objects
            this.formatDateValues( savedPatent );

            if ( this.patent.id ) {
                // percentage for at least on entry in inentors less than 0 or null
                if( this.patent.inventors.find(
                    ( inventor: PatentInventor ) => inventor.percentage === null || inventor.percentage === 0 ) ) {
                    this.notificationService.infoI18n( 'fs.res.patent.inventors.percentage.info' );
                }
                let showSuccessMessage = true;
                // save documentMetadata when patent.id is not null
                this.documentsHelperService.detectChangesAndSaveDocumentMetadata( this.documentsToSave ).subscribe(
                    (documentSaveSuccess: boolean) => {
                        if ( documentSaveSuccess ) {
                            this.documentsToSave = []; // trigger reload
                            // show save success message
                            if ( showSuccessMessage ) {
                                showSuccessMessage = false; // set showSuccessMesage to false, cause we want to show it only once
                                this.notificationService.successI18n( 'fs.res.patent.save.success' );
                                if ( !this.isEditMode ) {
                                    // switch from create mode to edit mode
                                    this.location.go( `/patent/edit/${this.patent.id}` );
                                }
                            }
                        }
                    }
                );
            }
        } );
    }

    /* reset inputs */
    public reset() {
        if ( this.patent.id ) {
            this.patentService.getPatentById( this.patent.id ).subscribe( (patent: Patent) => {
                this.patent = patent;
                this.formatDateValues( patent );
            } );
        } else {
            this.patent = new Patent();
            this.generateDynamicContent();
        }

        // reset priorityPatent values if current patent is sub patent
        if ( this.priorityPatentId ) {
            this.setPriorityPatentValues( this.priorityPatentId );
        }
        // reset documentsToSave
        this.documentsToSave = [];
    }

    /**
     * set values for (sub) patent with priority claim
     *
     * @param priorityPatent parent patent
     */
    private setPriorityPatentValues( priorityPatentId: number ) {
        this.patent.priorityPatent = new Patent( this.priorityPatentId, null, null, null );
        this.subscription = this.patentService.getPatentById( priorityPatentId ).subscribe( (priorityPatent: Patent) => {
            this.patent = { ...this.patent, priorityPatent };
            this.formatDateValues( this.patent );
            console.log( 'Loaded PriorityPatent: ', this.patent.priorityPatent );
        } );
    }

    /**
     * format string Dates to Date object
     *
     * @param patent Patent
     */
    private formatDateValues( patent: Patent ): void {
        this.patent.registrationDate = patent.registrationDate != null ? new Date( patent.registrationDate ) : null;
        this.patent.grantDate = patent.grantDate != null ? new Date( patent.grantDate ) : null;
        if ( this.patent.priorityPatent ) {
            // cause priorityPatent.registrationDate somehow is returned as number here
            this.patent.priorityPatent.registrationDate = this.patent.priorityPatent.registrationDate != null ?
                new Date( Number.parseInt( this.patent.priorityPatent.registrationDate.toString(), 10 ) ) : null;
        }
    }

    /* generate dynamic content */
    private generateDynamicContent() {
        this.dynamicContentService.generateDynamicContent( this.DATA_SERVICE_KEY_PATENT, this.getRequestParam() ).subscribe(
            (dC: DynamicComponent) => {
                // eslint-disable-next-line no-underscore-dangle
                this.patent._dynamicComponent = dC;
                this.reloadTrigger = !this.reloadTrigger; // reload trigger for dynamicComponent
            }
        );
    }

    /* patent specific dynamic content service request parameter */
    public getRequestParam(): string {
        if ( this.priorityPatentId ) {
            return 'parentId=' + this.priorityPatentId;
        }
        return null;
    }

    /* initialize default tab menu items */
    private initializeTabMenuItems() {
        this.tabMenuItems = [
            { i18nKey: 'fs.res.common.mainData' },
            { i18nKey: 'fs.res.common.classifications' },
            { i18nKey: 'fs.res.common.research' },
            { i18nKey: 'fs.res.patent.tab.financialData' }
        ];
    }

    // ngOnDestroy
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
